import { Injectable, Injector } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BrowserStorageService } from '../browser-storage/browser-storage.service';
import { AuthService } from '../Auth/auth.service';
declare let gtag: Function;
declare let fbq: Function;

// ga4tracking.service.ts
@Injectable({ providedIn: 'root' })
export class Ga4trackingService {
  private static readonly CT_QUEUE_KEY = 'ctQueuedEvents';

  constructor(
    private storageService: BrowserStorageService,
    private injector: Injector
  ) {}

  /**
   * The signed in user's own id: the same value the navbar gives CleverTap and the
   * same one the backend sends. Deliberately not the storage service's user id,
   * which holds the producer detail id for producers and would split one person
   * into two. Resolved through the injector rather than the constructor so this
   * service cannot take part in a construction cycle. Empty while the profile is
   * still loading, which makes those few events anonymous rather than misattributed.
   */
  private currentUserId(): string {
    try {
      const auth: any = this.injector.get(AuthService);
      return auth?.currentUserDetails?.value?._id || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Sends the same event to PostHog that the other three channels already receive.
   * Posted with fetch rather than the SDK on purpose: no new dependency, nothing
   * added to the bundle, and no autocapture. The id is the one the interceptor
   * sends to the backend, so a frontend event and a backend event for the same
   * visitor land on the same person. A signed out visitor is sent as an anonymous
   * event, which creates no profile. Gated on the key rather than on
   * environment.production, so a development project can be used while testing.
   */
  private sendToPostHog(eventName: string, eventParams: any) {
    try {
      if (!environment.postHogKey || !eventName) return;

      const userId = this.currentUserId();
      const distinctId = userId || this.storageService.getPostHogDistinctId();
      if (!distinctId) return;

      const properties: any = { ...eventParams, platform: 'web' };
      try {
        properties.$current_url = window.location.href;
      } catch (e) {}
      if (!userId) properties.$process_person_profile = false;

      // keepalive lets an event raised during navigation still leave the page.
      fetch(`${environment.postHogHost}/i/v0/e/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: environment.postHogKey,
          event: eventName,
          distinct_id: distinctId,
          properties,
          timestamp: new Date().toISOString(),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {}
  }

  private storeEvent(eventName: string, eventParams: any) {
    // fallback storage in localStorage if the SDK is not ready
    try {
      const current = JSON.parse(localStorage.getItem(Ga4trackingService.CT_QUEUE_KEY) || '[]');
      current.push({ eventName, eventParams });
      localStorage.setItem(Ga4trackingService.CT_QUEUE_KEY, JSON.stringify(current));
    } catch (e) {
      console.error('Could not queue event in localStorage', e);
    }
  }

  flushQueuedEvents() {
    try {
      const queued: Array<{ eventName: string; eventParams: any }> =
        JSON.parse(localStorage.getItem(Ga4trackingService.CT_QUEUE_KEY) || '[]');
      const ct = (window as any).clevertap;
      if (ct && Array.isArray(ct.event)) {
        queued.forEach(({ eventName, eventParams }) => {
          ct.event.push(eventName, eventParams);
        });
      }
      localStorage.removeItem(Ga4trackingService.CT_QUEUE_KEY);
    } catch (e) {
      console.error('Failed to flush queued events', e);
    }
  }

  customGa4Tracking(eventType: string, method: string, attributes: any) {
    // GA4 and Meta Pixel logic unchanged…
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ event: eventType, method, attributes });
    if (environment.production) {
      fbq('trackCustom', eventType, attributes);
    }

    // CleverTap logic:
    const ct = (window as any).clevertap;
    if (ct && Array.isArray(ct.event)) {
      ct.event.push({ action: eventType, ...attributes });
    } else {
      // SDK not ready: persist event
      this.storeEvent(eventType, attributes);
    }

    this.sendToPostHog(eventType, attributes);
  }

  sendEvent(eventName: string, eventParams: any = {} ) {
    // GA4 and Meta Pixel
    gtag('event', eventName, eventParams);
    if (environment.production) {
      fbq('trackCustom', eventName, eventParams);
    }

    // CleverTap
    const ct = (window as any).clevertap;
    if (ct && Array.isArray(ct.event)) {
      ct.event.push(eventName, eventParams);
    } else {
      this.storeEvent(eventName, eventParams);
    }

    this.sendToPostHog(eventName, eventParams);
  }
  sendStandardEvent(eventName: string, eventParams: any = {} ) {
    // Meta Pixel
    fbq('track', eventName, eventParams);
  }
}
